import { CookieService } from "ng2-cookies";
import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { RouterModule, Routes, Router } from "@angular/router";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";

import { HttpClientInMemoryWebApiModule } from "angular-in-memory-web-api";

import "hammerjs";
import { NgbDateParserFormatter, NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { ToastrModule } from "ngx-toastr";
import { TranslateModule } from "@ngx-translate/core";
import { ContextMenuModule } from "@ctrl/ngx-rightclick";

import { CoreModule } from "@core/core.module";
import { CoreCommonModule } from "@core/common.module";
import { CoreSidebarModule, CoreThemeCustomizerModule } from "@core/components";
import { CardSnippetModule } from "@core/components/card-snippet/card-snippet.module";

import { coreConfig } from "app/app-config";
import { AuthGuard } from "app/auth/helpers/auth.guards";
import { JwtInterceptor, ErrorInterceptor } from "app/auth/helpers";
import { AppComponent } from "app/app.component";
import { LayoutModule } from "app/layout/layout.module";
import { ContentHeaderModule } from "app/layout/components/content-header/content-header.module";
import { AuthConfigModule } from "./auth/configs/auth-config.module";
import { LoggerModule, NgxLoggerLevel } from "ngx-logger";
import { NgbDateCustomParserFormatter } from "./services/NgbDateCustomParserFormatter";
import { environment } from "environments/environment";
import { EConnectWebSocketLocalModule } from "./services/e-connect-web-socket-local/lib/e-connect-web-socket-local.module";
import { EConnectWebSocketModule } from "./services/e-connect-web-socket/e-connect-web-socket.module";
import { DatePipe, DecimalPipe } from "@angular/common";
import { ImageCheckPipe } from "./common/pipes/image-check.pipe";
import { VersionCheckService } from "./services/configuration/version-check.service";
import * as Sentry from "@sentry/angular";
import { StringToContentPipe } from "./common/pipes/stringToContent";

const appRoutes: Routes = [
  {
    path: "pages",
    loadChildren: () =>
      import("./main/pages/pages.module").then((m) => m.PagesModule),
  },
  {
    path: "worker",
    loadChildren: () =>
      import("./main/worker/worker-main.module").then(
        (m) => m.WorkerMainModule
      ),
  },
  {
    path: "configuration",
    loadChildren: () =>
      import("./main/pages/configuration/configuration.module").then(
        (m) => m.ConfigurationModule
      ),
  },
  {
    path: "tools",
    loadChildren: () =>
      import("./layout/layout.module").then((m) => m.LayoutModule),
  },
  {
    path: "",
    redirectTo: "/pages/worker-code",
    pathMatch: "full",
  },
  {
    path: "**",
    redirectTo: "/pages/miscellaneous/error", //Error 404 - Page not found
  },
];

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    RouterModule.forRoot(appRoutes, {
      scrollPositionRestoration: "enabled",
      relativeLinkResolution: "legacy",
      useHash: true,
    }),
    NgbModule,
    ToastrModule.forRoot(),
    TranslateModule.forRoot(),
    ContextMenuModule,
    CoreModule.forRoot(coreConfig),
    CoreCommonModule,
    CoreSidebarModule,
    CoreThemeCustomizerModule,
    CardSnippetModule,
    LayoutModule,
    ContentHeaderModule,
    AuthConfigModule,
    LoggerModule.forRoot({
      serverLoggingUrl: "/api/logs",
      level: NgxLoggerLevel.DEBUG,
      serverLogLevel: NgxLoggerLevel.ERROR,
    }),
    EConnectWebSocketModule.forRoot(environment),
    EConnectWebSocketLocalModule.forRoot(environment),
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: NgbDateParserFormatter, useClass: NgbDateCustomParserFormatter },
    CookieService,
    DatePipe,
    DecimalPipe,
    ImageCheckPipe,
    StringToContentPipe,
    VersionCheckService,
    { provide: LOCALE_ID, useValue: "es-ES" },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    }, {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
